import type { WhoamiDto } from "../../shared/WhoamiDto";
import type { IAccessTokenProvider } from "../interfaces/IAccessTokenProvider";
import type { ITokenApi } from "../interfaces/ITokenApi";
import type { IWhoamiApi } from "../interfaces/IWhoamiApi";
import type { IUrlStore } from "../interfaces/IUrlStore";

// Diese Promise wird von ALLEN Webcomponents auf der gleichen Seite verwendet werden
// Quasi wie ein singleton
let whoamiPromise: Promise<WhoamiDto | null> | null = null;

export class UserService  {
  readonly whoamiApi: IWhoamiApi;
  readonly tokenApi: ITokenApi;
  readonly tokenProvider: IAccessTokenProvider;
  readonly urlStore: IUrlStore;

  constructor(
    whoamiApi: IWhoamiApi,
    tokenApi: ITokenApi,
    tokenProvider: IAccessTokenProvider,
    urlStore: IUrlStore,
  ) {
    this.whoamiApi = whoamiApi;
    this.tokenApi = tokenApi;
    this.tokenProvider = tokenProvider;
    this.urlStore = urlStore;
  }
  // HeartBeat prüft, ob die App eingeloggt sein müsst aber nicht ist.
  // Führt wenn nötig den Refresh-Token-Prozess aus
  // oder loggt manuell aus.
  // Soll nur von der Login-Component ausgeführt werden!
  async getUserInfo(): Promise<WhoamiDto | null> {
    if (whoamiPromise) {
      //console.log("reuse whoamiPromise")
      return whoamiPromise;
    }
    //console.log("new whoamiPromise")
    whoamiPromise = (async () => {
      try {
        // check if token has expired
        let accessToken = await this.tokenProvider.getAccessToken();
        let expired = await this.tokenProvider.isAccessTokenExpired();
        //console.log("expired", expired)
        if (accessToken == null){
          //console.log("access token null")
          return null
        }
        if (expired){
          let tokenDto = await this.tokenApi.getTokenForRefreshToken(accessToken);
          await this.tokenProvider.saveToken(tokenDto);
        } 
        //console.log("call whoami")
        let whoami = await this.whoamiApi.whoami();
        return whoami;
      } finally {
        // Egal ob success oder error → Promise zurücksetzen
        //console.log("close whoamiPromise")
        whoamiPromise = null;
      }
    })();
    return whoamiPromise;
  }

  async isLoggedIn(): Promise<boolean> {
    const whoami = await this.whoamiApi.whoami();
    return whoami?.isLoggedIn ?? false;
  }
  async logout(): Promise<void> {
    await this.tokenProvider.clearToken();
  }

  async getLoginUrl(): Promise<string> {
    const idp = await this.tokenApi.getIdp();
    return idp.loginUrl;
  }
  async getLogoutUrl(): Promise<string> {
    const idp = await this.tokenApi.getIdp();
    return idp.logoutUrl;
  }
  async processLoginCode(code: string): Promise<void> {
    let tokenDto = await this.tokenApi.getTokenForCode(code);
    await this.tokenProvider.saveToken(tokenDto);
  }

  async setAfterLoginUrl(url: string): Promise<void> {
    await this.urlStore.saveAfterLoginUrl(url);
  }
  async getAfterLoginUrl(): Promise<string | null> {
    return await this.urlStore.loadAfterLoginUrl();
  }
  async clearAfterLoginUrl(): Promise<void> {
    await this.urlStore.clearAfterLoginUrl();
  }
}
